/**
 * Loading Components
 * Feature: 012-minimal-modern-ui
 *
 * Minimal modern loading states using Signal Flow branding:
 * 1. SignalFlowSpinner - Animated 4-bar signal flow (React component)
 * 2. TabLoadingSpinner - Content area loading
 * 3. FullscreenLoading - App initialization (uses SVG file)
 * 4. LoadingSpinner - Simple spinner for inline use
 * 
 * Note: Minimum loading time (2000ms) is handled in App.tsx for fullscreen loading
 */

import { SignalFlowSpinner } from './TalksyLogo';

interface LoadingSpinnerProps {
  /** Size of the spinner */
  size?: 'sm' | 'md' | 'lg';
  /** Custom className */
  className?: string;
}

const sizeClasses = {
  sm: 'w-4 h-4',
  md: 'w-8 h-8',
  lg: 'w-12 h-12',
} as const;

/**
 * Simple loading spinner (border-based)
 */
export function LoadingSpinner({ 
  size = 'md', 
  className = '',
}: LoadingSpinnerProps) {
  return (
    <div
      className={`
        rounded-full animate-spin
        border-2 border-indigo-500 border-t-transparent
        ${sizeClasses[size]}
        ${className}
      `}
      role="status"
      aria-label="Loading"
    >
      <span className="sr-only">Loading...</span>
    </div>
  );
}

/**
 * Tab/Content area loading spinner
 * Uses Signal Flow animated spinner
 */
interface TabLoadingSpinnerProps {
  /** Show skeleton background placeholders */
  showSkeleton?: boolean;
  /** Size of the spinner */
  size?: 'sm' | 'md' | 'lg';
}

export function TabLoadingSpinner({ showSkeleton = false, size = 'md' }: TabLoadingSpinnerProps) {
  return (
    <div className="flex-1 relative overflow-hidden flex flex-col">
      {/* Centered Signal Flow spinner */}
      <div className="absolute inset-0 z-50 flex items-center justify-center pointer-events-none">
        <SignalFlowSpinner size={size} />
      </div>

      {/* Skeleton background (optional) */}
      {showSkeleton && (
        <div className="flex-1 overflow-y-hidden px-2 py-4 space-y-6 opacity-40">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-start gap-4 px-4 py-2">
              <div className="w-10 h-10 rounded-full bg-gray-700/50 flex-shrink-0 animate-pulse" />
              <div className="flex-1 min-w-0 space-y-2 py-1">
                <div className="flex items-center gap-3">
                  <div className={`h-4 bg-gray-700/50 rounded animate-pulse`} style={{ width: `${60 + (i * 10) % 40}px` }} />
                  <div className="h-3 w-10 bg-gray-800/50 rounded animate-pulse" />
                </div>
                <div className="space-y-1.5">
                  <div className="h-3 bg-gray-800/50 rounded animate-pulse" style={{ width: `${50 + (i * 15) % 50}%` }} />
                  {i % 2 === 0 && (
                    <div className="h-3 bg-gray-800/50 rounded animate-pulse" style={{ width: `${30 + (i * 10) % 40}%` }} />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Fullscreen loading splash
 * App initialization - Uses SVG file from public/loading-spinner.svg
 * Note: Minimum display time (2000ms) is controlled by App.tsx
 */
export function FullscreenLoading() {
  return (
    <div className="bg-bg-primary h-screen w-screen overflow-hidden flex items-center justify-center">
      <img 
        src="/loading-spinner.svg" 
        alt="Loading" 
        className="w-24 h-20"
        aria-label="Loading"
      />
    </div>
  );
}

/**
 * Legacy support - Full page spinner
 * @deprecated Use FullscreenLoading instead
 */
export function FullPageSpinner() {
  return <FullscreenLoading />;
}

// Re-export SignalFlowSpinner for direct use
export { SignalFlowSpinner };
